(function () {
  'use strict';

  var API_ORIGIN = 'https://apiv2.easyneonsigns.ca';
  var SESSION_ENDPOINT = API_ORIGIN + '/form/session';
  var DESIGNER_SESSION_ENDPOINT = API_ORIGIN + '/designer/session';
  var SUBMIT_ENDPOINT = API_ORIGIN + '/submit-form';
  var SCHOOL_SUBMIT_ENDPOINT = API_ORIGIN + '/submit-school-form';
  var SESSION_TIMEOUT_MS = 10000;
  var SUBMIT_TIMEOUT_MS = 45000;
  var SESSION_RESPONSE_LIMIT = 16 * 1024;
  var SUBMIT_RESPONSE_LIMIT = 32 * 1024;
  var SESSION_EXPIRY_SKEW_MS = 15000;
  var MAX_RETRY_AFTER_MS = 5 * 60 * 1000;
  var sessionsByOperation = new Map();
  var attemptsByForm = new WeakMap();
  var rateLimitedUntil = 0;

  window.ENS_UNIFIED_QUOTE = true;

  function locale() {
    return document.documentElement.lang.toLowerCase().indexOf('fr') === 0 ? 'fr' : 'en';
  }

  function text(en, fr) {
    return locale() === 'fr' ? fr : en;
  }

  function createFormError(code, message, status) {
    var error = new Error(message || code);
    error.code = code;
    if (status) error.status = status;
    return error;
  }

  function createNonce(prefix) {
    var suffix = '';
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      suffix = window.crypto.randomUUID();
    } else if (window.crypto && typeof window.crypto.getRandomValues === 'function') {
      var values = new Uint32Array(4);
      window.crypto.getRandomValues(values);
      suffix = Array.prototype.map.call(values, function (value) {
        return value.toString(16).padStart(8, '0');
      }).join('');
    } else {
      suffix = Date.now().toString(36) + '-' + Math.random().toString(36).slice(2) + '-' + Math.random().toString(36).slice(2);
    }
    var normalizedPrefix = String(prefix || 'form').replace(/[^A-Za-z0-9._:-]/g, '-').slice(0, 40);
    var nonce = (normalizedPrefix + '-' + suffix).slice(0, 128);
    if (!/^[A-Za-z0-9._:-]{16,128}$/.test(nonce)) {
      throw createFormError('form_nonce_unavailable', 'Unable to create a secure request identifier.');
    }
    return nonce;
  }

  function clearAttempt(form) {
    var attempt = attemptsByForm.get(form);
    if (attempt) clearSessionsForOperation(attempt.key);
    attemptsByForm.delete(form);
  }

  function clearSessionsForOperation(operationKey) {
    sessionsByOperation.forEach(function (_session, cacheKey) {
      if (cacheKey.slice(-(operationKey.length + 1)) === '|' + operationKey) sessionsByOperation.delete(cacheKey);
    });
  }

  function bindAttemptInvalidation(form) {
    if (form.dataset.ensAttemptBound === 'true') return;
    form.dataset.ensAttemptBound = 'true';
    var invalidate = function () {
      var attempt = attemptsByForm.get(form);
      if (!attempt) return;
      if (attempt.inFlight) {
        attempt.dirtyDuringFlight = true;
        return;
      }
      clearAttempt(form);
    };
    form.addEventListener('input', invalidate);
    form.addEventListener('change', invalidate);
  }

  function getAttempt(form, prefix) {
    bindAttemptInvalidation(form);
    var attempt = attemptsByForm.get(form);
    if (!attempt) {
      attempt = { key: createNonce(prefix || 'ens-quote'), inFlight: false, dirtyDuringFlight: false };
      attemptsByForm.set(form, attempt);
    }
    return attempt;
  }

  function lockFormForRequest(form) {
    var controls = [].slice.call(form.querySelectorAll('input, select, textarea, button'));
    var enabledControls = controls.filter(function (control) { return !control.disabled; });
    var previousBusy = form.hasAttribute('aria-busy') ? form.getAttribute('aria-busy') : null;
    form.setAttribute('aria-busy', 'true');
    enabledControls.forEach(function (control) { control.disabled = true; });
    return { enabledControls: enabledControls, previousBusy: previousBusy };
  }

  function unlockFormAfterRequest(form, lock) {
    lock.enabledControls.forEach(function (control) { control.disabled = false; });
    if (lock.previousBusy === null) form.removeAttribute('aria-busy');
    else form.setAttribute('aria-busy', lock.previousBusy);
  }

  function parseRetryAfter(response) {
    var raw = response && response.headers && response.headers.get('Retry-After');
    if (!raw || raw.length > 64) return 0;
    var seconds = Number(raw);
    var delay = Number.isFinite(seconds) ? seconds * 1000 : Date.parse(raw) - Date.now();
    if (!Number.isFinite(delay) || delay <= 0) return 0;
    return Math.min(Math.ceil(delay), MAX_RETRY_AFTER_MS);
  }

  async function readBoundedJson(response, byteLimit) {
    var contentType = response.headers && response.headers.get('Content-Type');
    if (contentType && (contentType.length > 128 || !/^application\/(?:[a-z0-9.+-]*\+)?json(?:\s*;|$)/i.test(contentType))) {
      if (response.body && typeof response.body.cancel === 'function') await response.body.cancel().catch(function () {});
      throw createFormError('form_response_invalid', 'The service returned an invalid response.');
    }
    var contentLength = Number(response.headers && response.headers.get('Content-Length'));
    if (Number.isFinite(contentLength) && contentLength > byteLimit) {
      if (response.body && typeof response.body.cancel === 'function') await response.body.cancel().catch(function () {});
      throw createFormError('form_response_too_large', 'The service returned an invalid response.');
    }

    var bodyText = '';
    if (response.body && typeof response.body.getReader === 'function' && typeof TextDecoder !== 'undefined') {
      var reader = response.body.getReader();
      var decoder = new TextDecoder();
      var total = 0;
      while (true) {
        var part = await reader.read();
        if (part.done) break;
        total += part.value.byteLength;
        if (total > byteLimit) {
          await reader.cancel().catch(function () {});
          throw createFormError('form_response_too_large', 'The service returned an invalid response.');
        }
        bodyText += decoder.decode(part.value, { stream: true });
      }
      bodyText += decoder.decode();
    } else {
      bodyText = await response.text();
      if (new Blob([bodyText]).size > byteLimit) {
        throw createFormError('form_response_too_large', 'The service returned an invalid response.');
      }
    }

    if (!bodyText.trim()) return {};
    try {
      var parsed = JSON.parse(bodyText);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) throw new Error('Expected an object');
      return parsed;
    } catch (_error) {
      throw createFormError('form_response_invalid', 'The service returned an invalid response.');
    }
  }

  async function requestJson(url, options, timeoutMs, responseLimit) {
    var controller = new AbortController();
    var timer = setTimeout(function () { controller.abort(); }, timeoutMs);
    try {
      var requestOptions = Object.assign({}, options, { signal: controller.signal });
      var response = await fetch(url, requestOptions);
      var data = await readBoundedJson(response, responseLimit);
      return { response: response, data: data };
    } catch (error) {
      if (error && (error.name === 'AbortError' || controller.signal.aborted)) {
        throw createFormError('form_timeout', 'The request timed out. Please try again.');
      }
      throw error;
    } finally {
      clearTimeout(timer);
    }
  }

  function pruneSessions() {
    var now = Date.now();
    sessionsByOperation.forEach(function (session, key) {
      if (!session.pending && session.expiresAt <= now) sessionsByOperation.delete(key);
    });
  }

  async function getScopedSession(operationKey, sessionEndpoint, requiredScope, turnstileToken, forceRefresh) {
    pruneSessions();
    var cacheKey = sessionEndpoint + '|' + operationKey;
    if (forceRefresh) sessionsByOperation.delete(cacheKey);
    var existing = sessionsByOperation.get(cacheKey);
    if (existing && existing.accessToken && existing.expiresAt > Date.now() + SESSION_EXPIRY_SKEW_MS) return existing;
    if (existing && existing.pending) return existing.pending;

    var session = existing || { accessToken: null, expiresAt: 0, pending: null };
    var body = { client_nonce: createNonce('form-session') };
    if (turnstileToken && /^[A-Za-z0-9._~-]{10,4096}$/.test(turnstileToken)) body.turnstile_token = turnstileToken;

    session.pending = requestJson(sessionEndpoint, {
      method: 'POST',
      headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    }, SESSION_TIMEOUT_MS, SESSION_RESPONSE_LIMIT).then(function (result) {
      var data = result.data;
      if (!result.response.ok) {
        var sessionError = createFormError('form_session_invalid', 'Unable to verify this form request.', result.response.status);
        sessionError.response = result.response;
        sessionError.data = data;
        sessionError.retryAfterMs = result.response.status === 429 ? parseRetryAfter(result.response) : 0;
        if (sessionError.retryAfterMs) rateLimitedUntil = Date.now() + sessionError.retryAfterMs;
        throw sessionError;
      }
      var expiresIn = Number(data.expires_in);
      if (
        data.token_type !== 'Bearer'
        || typeof data.access_token !== 'string'
        || !/^[A-Za-z0-9._~-]{32,4096}$/.test(data.access_token)
        || !Number.isFinite(expiresIn)
        || expiresIn <= 0
        || expiresIn > 300
        || !Array.isArray(data.scopes)
        || data.scopes.indexOf(requiredScope) === -1
      ) {
        throw createFormError('form_session_invalid', 'Unable to verify this form request.', result.response.status);
      }
      session.accessToken = data.access_token;
      session.expiresAt = Date.now() + (expiresIn * 1000);
      return session;
    }).finally(function () {
      session.pending = null;
    });
    sessionsByOperation.set(cacheKey, session);
    return session.pending;
  }

  function getTurnstileToken(form) {
    var field = form.querySelector('[name="turnstile_token"], [name="cf-turnstile-response"]');
    return field && typeof field.value === 'string' ? field.value.trim() : '';
  }

  async function submitScopedForm(endpoint, form, formData, options) {
    options = options || {};
    var attempt = getAttempt(form, options.prefix || 'ens-quote');
    var operationKey = attempt.key;
    var turnstileToken = getTurnstileToken(form);
    var refreshedAuthentication = false;
    var refreshedConsumedScope = false;
    var formLock = lockFormForRequest(form);
    attempt.inFlight = true;

    try {
      if (rateLimitedUntil > Date.now()) {
        var limited = createFormError('form_rate_limited', 'Please wait before trying again.', 429);
        limited.retryAfterMs = rateLimitedUntil - Date.now();
        limited.idempotencyKey = operationKey;
        throw limited;
      }

      while (true) {
        var session = await getScopedSession(operationKey, SESSION_ENDPOINT, 'form:submit', turnstileToken, false);
        var result = await requestJson(endpoint, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Authorization': 'Bearer ' + session.accessToken,
            'X-Idempotency-Key': operationKey
          },
          body: formData
        }, SUBMIT_TIMEOUT_MS, SUBMIT_RESPONSE_LIMIT);

        if ((result.response.status === 401 || result.response.status === 403) && !refreshedAuthentication) {
          refreshedAuthentication = true;
          await getScopedSession(operationKey, SESSION_ENDPOINT, 'form:submit', turnstileToken, true);
          continue;
        }
        var consumedScope = result.response.status === 409
          && result.data
          && result.data.detail
          && result.data.detail.code === 'session_scope_consumed';
        if (consumedScope && !refreshedConsumedScope) {
          refreshedConsumedScope = true;
          await getScopedSession(operationKey, SESSION_ENDPOINT, 'form:submit', turnstileToken, true);
          continue;
        }
        var retryAfterMs = result.response.status === 429 ? parseRetryAfter(result.response) : 0;
        if (retryAfterMs) rateLimitedUntil = Date.now() + retryAfterMs;
        return {
          response: result.response,
          data: result.data,
          idempotencyKey: operationKey,
          retryAfterMs: retryAfterMs
        };
      }
    } catch (error) {
      error.idempotencyKey = operationKey;
      throw error;
    } finally {
      attempt.inFlight = false;
      unlockFormAfterRequest(form, formLock);
      if (attempt.dirtyDuringFlight) clearAttempt(form);
    }
  }

  function submitForm(form, formData, options) {
    return submitScopedForm(SUBMIT_ENDPOINT, form, formData, options);
  }

  function submitSchoolForm(form, formData, options) {
    return submitScopedForm(SCHOOL_SUBMIT_ENDPOINT, form, formData, options);
  }

  async function designerRequest(endpoint, payload, options) {
    options = options || {};
    var allowed = {
      '/apply-discount': 'discount:validate',
      '/create-draft-order': 'draft:create'
    };
    var path = String(endpoint || '').replace(API_ORIGIN, '');
    var requiredScope = allowed[path];
    if (!requiredScope) throw createFormError('designer_endpoint_invalid', 'Unsupported designer endpoint.');
    var operationKey = options.idempotencyKey || createNonce(options.prefix || 'designer-operation');
    if (!/^[A-Za-z0-9._:-]{16,128}$/.test(operationKey)) {
      throw createFormError('designer_operation_invalid', 'Invalid designer operation identifier.');
    }
    var refreshedUnauthorized = false;
    var refreshedForbidden = false;
    while (true) {
      var session = await getScopedSession(operationKey, DESIGNER_SESSION_ENDPOINT, requiredScope, '', false);
      var result = await requestJson(API_ORIGIN + path, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Authorization': 'Bearer ' + session.accessToken,
          'Content-Type': 'application/json',
          'X-Idempotency-Key': operationKey
        },
        body: JSON.stringify(payload)
      }, SUBMIT_TIMEOUT_MS, SUBMIT_RESPONSE_LIMIT);
      if (result.response.status === 401 && !refreshedUnauthorized) {
        refreshedUnauthorized = true;
        await getScopedSession(operationKey, DESIGNER_SESSION_ENDPOINT, requiredScope, '', true);
        continue;
      }
      if (result.response.status === 403 && !refreshedForbidden) {
        refreshedForbidden = true;
        await getScopedSession(operationKey, DESIGNER_SESSION_ENDPOINT, requiredScope, '', true);
        continue;
      }
      return {
        response: result.response,
        data: result.data,
        idempotencyKey: operationKey,
        retryAfterMs: result.response.status === 429 ? parseRetryAfter(result.response) : 0
      };
    }
  }

  function safeApiMessage(data) {
    var candidate = data && data.error;
    if (typeof candidate !== 'string') return '';
    var normalized = candidate.replace(/[\u0000-\u001f\u007f]+/g, ' ').replace(/\s+/g, ' ').trim();
    return normalized && normalized.length <= 240 && normalized.indexOf('<') === -1 && normalized.indexOf('>') === -1
      ? normalized
      : '';
  }

  function userMessage(subject, language) {
    var fr = language === 'fr';
    var response = subject && subject.response;
    var data = subject && subject.data;
    var status = Number(subject && subject.status || response && response.status || 0);
    var code = subject && subject.code;
    var retryAfterMs = Number(subject && subject.retryAfterMs || 0);
    if ((status === 400 || status === 422) && safeApiMessage(data)) return safeApiMessage(data);
    if (code === 'form_timeout') return fr ? 'La demande a expiré. Veuillez réessayer.' : 'The request timed out. Please try again.';
    if (status === 429 || code === 'form_rate_limited') {
      var seconds = retryAfterMs > 0 ? Math.max(1, Math.ceil(retryAfterMs / 1000)) : 0;
      if (seconds) return fr ? 'Trop de demandes. Réessayez dans ' + seconds + ' secondes.' : 'Too many requests. Try again in ' + seconds + ' seconds.';
      return fr ? 'Trop de demandes. Veuillez patienter avant de réessayer.' : 'Too many requests. Please wait before trying again.';
    }
    if (status === 503) return fr ? 'Le service est temporairement indisponible. Veuillez réessayer.' : 'The service is temporarily unavailable. Please try again.';
    if (status === 403 || code === 'form_session_invalid') return fr ? 'Nous n’avons pas pu vérifier cette demande. Actualisez la page et réessayez.' : 'We could not verify this request. Refresh the page and try again.';
    if (status === 409) return fr ? 'Cette demande n’a pas pu être confirmée. Veuillez réessayer.' : 'We could not confirm this request. Please try again.';
    return fr ? 'Un problème est survenu. Veuillez réessayer.' : 'Something went wrong. Please try again.';
  }

  window.EasyNeonFormSession = Object.freeze({
    submit: submitForm,
    submitSchool: submitSchoolForm,
    designerRequest: designerRequest,
    createOperationKey: createNonce,
    complete: clearAttempt,
    getUserMessage: userMessage,
    getSafeApiMessage: safeApiMessage
  });

  function showSuccess(form) {
    var panel = document.createElement('section');
    panel.className = 'ens-quote-success';
    panel.setAttribute('role', 'status');
    panel.setAttribute('aria-live', 'polite');
    panel.setAttribute('aria-atomic', 'true');
    panel.setAttribute('tabindex', '-1');
    var icon = document.createElement('span');
    icon.className = 'ens-quote-success__icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.textContent = '✓';
    var heading = document.createElement('h2');
    heading.className = 'ens-quote-success__title';
    heading.textContent = text('Quote request received.', 'Demande de devis reçue.');
    var message = document.createElement('p');
    message.className = 'ens-quote-success__message';
    message.textContent = text(
      'Thank you! We’ll review your sign details and follow up by email with the next steps.',
      'Merci ! Nous examinerons les détails de votre enseigne et vous contacterons par courriel pour la suite.'
    );
    panel.appendChild(icon);
    panel.appendChild(heading);
    panel.appendChild(message);
    var previous = form.querySelector('.ens-quote-success');
    if (previous) previous.remove();
    form.appendChild(panel);
    form.classList.add('is-submitted');
    var dialog = form.closest('.ens-qm-dialog');
    if (dialog) dialog.classList.add('is-quote-complete');
    panel.focus({ preventScroll: true });
    var rect = panel.getBoundingClientRect();
    if (rect.top < 0 || rect.bottom > window.innerHeight) panel.scrollIntoView({ behavior: 'auto', block: 'nearest' });
  }

  function stepForm(form) {
    var current = 1;
    var steps = [].slice.call(form.querySelectorAll('[data-quote-step]'));
    var next = form.querySelector('[data-quote-next]');
    var back = form.querySelector('[data-quote-back]');
    var submit = form.querySelector('.ens-quote-submit');
    var reassurance = form.querySelector('[data-quote-submit-reassurance]');
    function progress() {
      var root = form.dataset.mode === 'modal' ? form.closest('.ens-qm-dialog') : form;
      return root ? [].slice.call(root.querySelectorAll('.ens-qm-progress span,.ens-quote-progress span')) : [];
    }
    function draw() {
      steps.forEach(function (step) { step.hidden = Number(step.dataset.quoteStep) !== current; });
      next.hidden = current === steps.length;
      back.hidden = current === 1;
      submit.hidden = current !== steps.length;
      if (reassurance) reassurance.hidden = current !== steps.length;
      progress().forEach(function (item, index) {
        item.classList.toggle('is-active', index < current);
        if (index === current - 1) item.setAttribute('aria-current', 'step');
        else item.removeAttribute('aria-current');
      });
    }
    function reveal() {
      var step = steps[current - 1];
      var dialog = form.closest('.ens-qm-dialog');
      if (dialog) {
        dialog.scrollTop = 0;
        return;
      }
      var heading = step.querySelector('.ens-quote-step-title,p') || step;
      heading.setAttribute('tabindex', '-1');
      heading.focus({ preventScroll: true });
      var rect = heading.getBoundingClientRect();
      if (rect.top < 0 || rect.bottom > window.innerHeight) heading.scrollIntoView({ behavior: 'auto', block: 'nearest' });
    }
    function valid(all) {
      var area = all ? form : steps[current - 1];
      var required = [].slice.call(area.querySelectorAll('[required]'));
      var ok = true;
      required.forEach(function (element) {
        var group = element.name && form.querySelectorAll('[name="' + element.name + '"]');
        if (group && group.length > 1) {
          if (![].some.call(group, function (item) { return item.checked; })) ok = false;
        } else if (!element.checkValidity()) ok = false;
      });
      if (!ok) {
        form.querySelector('[data-quote-error]').textContent = text('Please complete the required fields before continuing.', 'Veuillez remplir les champs obligatoires avant de continuer.');
        var bad = area.querySelector(':invalid') || area.querySelector('[name="installation_environment"]');
        if (bad) bad.focus();
      } else form.querySelector('[data-quote-error]').textContent = '';
      return ok;
    }
    if (next) next.addEventListener('click', function () { if (valid()) { current += 1; draw(); reveal(); } });
    if (back) back.addEventListener('click', function () { if (current > 1) { current -= 1; draw(); reveal(); } });
    draw();
    return valid;
  }

  function analytics(form, data, key) {
    if (form.dataset.lastTrackedKey === key) return;
    form.dataset.lastTrackedKey = key;
    var formData = new FormData(form);
    var fileInput = form.querySelector('[name="file"]');
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'ens_quote_submit_success',
      request_id: data.request_id || '',
      form_id: form.id || 'ens-quote-form',
      form_source: formData.get('form_source') || '',
      locale: locale(),
      page_type: document.body.dataset.template || '',
      page_handle: location.pathname,
      product_handle: location.pathname.split('/products/')[1] || '',
      has_upload: !!(fileInput && fileInput.files && fileInput.files.length),
      installation_environment: formData.get('installation_environment') || '',
      mounting_method: formData.get('mounting_method') || '',
      dimensions_known: !!(formData.get('sign_width') || formData.get('sign_height'))
    });
  }

  function bindQuoteSubmit(form, valid) {
    form.addEventListener('submit', async function (event) {
      event.preventDefault();
      if (!valid(true)) return;
      var button = form.querySelector('[type="submit"]');
      if (button.disabled) return;
      button.disabled = true;
      var original = button.textContent;
      button.textContent = text('Submitting…', 'Envoi en cours…');
      var formData = new FormData(form);
      formData.set('message', formData.get('sign_text') || formData.get('additional_notes') || 'Quote request');
      if (window.EasyNeonQuoteTracking) window.EasyNeonQuoteTracking.appendToFormData(formData);
      var source = formData.get('form_source') || 'unknown';
      var formId = form.id || 'ens-quote-form';
      try {
        var result = await submitForm(form, formData, { prefix: 'ens-quote' });
        if (!result.response.ok) {
          var requestError = createFormError('form_submit_failed', userMessage(result, locale()), result.response.status);
          requestError.result = result;
          throw requestError;
        }
        analytics(form, result.data, result.idempotencyKey);
        if (window.EasyNeonQuoteTracking) window.EasyNeonQuoteTracking.submitSuccess(formId, source, {
          quote_id: result.data.quote_id || result.data.request_id || '',
          status: result.data.status || 'success'
        });
        clearAttempt(form);
        form.reset();
        showSuccess(form);
      } catch (error) {
        var failureResult = error.result || error;
        var message = error.message && error.code === 'form_submit_failed'
          ? error.message
          : userMessage(failureResult, locale());
        if (window.EasyNeonQuoteTracking) window.EasyNeonQuoteTracking.submitFailure(formId, source, {
          error_message: message
        });
        var box = form.querySelector('[data-quote-error]');
        box.style.color = '#ff8d8d';
        box.textContent = message;
      } finally {
        button.disabled = false;
        button.textContent = original;
      }
    });
  }

  function init(form) {
    if (form.dataset.quoteReady) return;
    var valid = stepForm(form);
    bindQuoteSubmit(form, valid);
    form.dataset.quoteReady = '1';
  }

  function boot() {
    [].forEach.call(document.querySelectorAll('[data-ens-quote-form]'), init);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
